"use client";

import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useWalletPortfolioContext } from "../../providers/wallet-portfolio-provider";
import {
  CHAIN_IDS,
  CROSSD_POSITION_MANAGER,
  CROSSD_WCROSS,
} from "../../constants";
import type {
  CrossdPosition,
  CrossdPositionDetail,
  GasEstimate,
  SendTransactionArgs,
} from "../../types";
import {
  DEADLINE_SECONDS,
  applyWithdrawSlippage,
  composeV3WithdrawCalldata,
} from "../../v3-position-encoding";
import { simulateDecreaseLiquidity } from "../../queries/v3-simulation-rpc";
import { toDisplayWalletError } from "../../utils";
import {
  normalizeFailureReason,
  trackDappUiFunnel,
  type TrackDappUiFunnelOptions,
} from "../../../../lib/sentry/index";

const WP_QUERY_PREFIX = "wp:";
/** crossd처럼 인덱서 반영 지연을 고려해 시차를 두고 재조회한다. */
const REFETCH_DELAYS_MS = [1_500, 3_000, 4_500, 6_000] as const;

export type CrossdWithdrawStatus =
  | "confirm"
  | "estimating"
  | "submitting"
  | "success"
  | "error";

export interface CrossdWithdrawTarget {
  position: CrossdPosition;
  detail: CrossdPositionDetail;
}

export interface CrossdWithdrawPrepared {
  tx: SendTransactionArgs;
  estimate: GasEstimate | null;
  expectedAmount0: bigint;
  expectedAmount1: bigint;
}

function toHexAddress(address: string): `0x${string}` | null {
  return /^0x[a-fA-F0-9]{40}$/.test(address)
    ? (address as `0x${string}`)
    : null;
}

/**
 * CROSSD v3 포지션 전체 출금(유동성 100% 제거 + 수수료 수령) 상태 머신.
 * `useWithdrawFlow`(rewards/points)와 동일한 상태·퍼널 구조를 따르고,
 * 트랜잭션 빌드만 NonfungiblePositionManager multicall로 교체했다.
 */
export function useCrossdWithdrawFlow() {
  const { env, walletAddress, sendTransaction, estimateGas } =
    useWalletPortfolioContext();
  const canWithdrawInline = !!sendTransaction;
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = React.useState(false);
  const [target, setTarget] = React.useState<CrossdWithdrawTarget | null>(null);
  const [status, setStatus] = React.useState<CrossdWithdrawStatus>("confirm");
  const [txHash, setTxHash] = React.useState<`0x${string}` | undefined>();
  const [error, setError] = React.useState("");
  const [copied, setCopied] = React.useState(false);
  /**
   * 이번 세션에서 출금 성공한 포지션 token_id들. cross-defi 인덱서가 반영을
   * 따라잡기 전까지 API가 계속 내려주는 stale 포지션을 리스트에서 즉시
   * 숨기기 위한 낙관적 마킹 — API가 따라잡으면 자연히 무의미해진다.
   */
  const [withdrawnTokenIds, setWithdrawnTokenIds] = React.useState<
    ReadonlySet<string>
  >(new Set());
  const copyTimerRef = React.useRef<ReturnType<typeof setTimeout>>(undefined);
  const refetchTimersRef = React.useRef<ReturnType<typeof setTimeout>[]>([]);
  /** open/close마다 증가시켜, 닫힌 뒤 늦게 끝난 prepareWithdraw가 새 상태를 덮어쓰지 못하게 막는다. */
  const flowIdRef = React.useRef(0);

  React.useEffect(() => {
    return () => {
      clearTimeout(copyTimerRef.current);
      refetchTimersRef.current.forEach(clearTimeout);
    };
  }, []);

  const funnelFiredRef = React.useRef(new Set<string>());
  const trackFunnelOnce = React.useCallback(
    (funnelStep: string, funnelOptions?: TrackDappUiFunnelOptions) => {
      if (funnelFiredRef.current.has(funnelStep)) return;
      funnelFiredRef.current.add(funnelStep);
      trackDappUiFunnel("withdraw", funnelStep, {
        ...funnelOptions,
        tags: { section: "crossdPool", ...funnelOptions?.tags },
      });
    },
    [],
  );

  const invalidatePortfolioQueries = React.useCallback(() => {
    const invalidate = () =>
      queryClient.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey[0] === "string" &&
          query.queryKey[0].startsWith(WP_QUERY_PREFIX),
      });
    invalidate();
    refetchTimersRef.current.forEach(clearTimeout);
    refetchTimersRef.current = REFETCH_DELAYS_MS.map((delay) =>
      setTimeout(invalidate, delay),
    );
  }, [queryClient]);

  const openWithdraw = React.useCallback(
    (next: CrossdWithdrawTarget) => {
      flowIdRef.current += 1;
      setTarget(next);
      setIsOpen(true);
      setStatus("confirm");
      setTxHash(undefined);
      setError("");
      setCopied(false);
      funnelFiredRef.current = new Set();
      trackFunnelOnce("opened");
    },
    [trackFunnelOnce],
  );

  /** 전송 중(submitting)에만 닫기를 막는다. estimating은 시뮬레이션/가스 추정 대기일 뿐이라 취소 가능해야 한다. */
  const closeWithdraw = React.useCallback(() => {
    if (status === "submitting") return;
    flowIdRef.current += 1;
    setIsOpen(false);
    setTarget(null);
    setStatus("confirm");
    setTxHash(undefined);
    setError("");
    setCopied(false);
  }, [status]);

  /**
   * 시뮬레이션 → 슬리피지 1% → multicall calldata → 가스 추정.
   * 실패 시 status="error"로 두고 null 반환 — 호출부는 전송하지 않는다.
   */
  const prepareWithdraw =
    React.useCallback(async (): Promise<CrossdWithdrawPrepared | null> => {
      const flowId = flowIdRef.current;
      const owner = toHexAddress(walletAddress);
      const tokenIdRaw = target?.position.token_id;
      const liquidityRaw = target?.position.liquidity;
      const token0 = target?.detail.pool?.token0?.address;
      const token1 = target?.detail.pool?.token1?.address;

      if (!owner || !tokenIdRaw || !liquidityRaw || !token0 || !token1) {
        setStatus("error");
        setError("Position data is incomplete. Please refresh and retry.");
        return null;
      }

      try {
        setStatus("estimating");
        setError("");

        const tokenId = BigInt(tokenIdRaw);
        const liquidity = BigInt(liquidityRaw);
        const deadline =
          BigInt(Math.floor(Date.now() / 1000)) + DEADLINE_SECONDS;
        const positionManager = CROSSD_POSITION_MANAGER[env];

        const simulated = await simulateDecreaseLiquidity(env, {
          owner,
          positionManager,
          tokenId,
          liquidity,
          deadline,
        });
        if (flowIdRef.current !== flowId) return null; // 대기 중 닫힘/재오픈됨 — 새 플로우 상태를 건드리지 않는다.
        if (!simulated) {
          setStatus("error");
          setError("Failed to estimate withdrawal amounts. Please retry.");
          return null;
        }

        const data = composeV3WithdrawCalldata({
          tokenId,
          liquidity,
          amount0Min: applyWithdrawSlippage(simulated.amount0),
          amount1Min: applyWithdrawSlippage(simulated.amount1),
          deadline,
          owner,
          positionManagerAddress: positionManager,
          token0,
          token1,
          wcrossAddress: CROSSD_WCROSS[env],
        });

        const tx: SendTransactionArgs = {
          to: positionManager,
          value: 0n,
          data,
          chainId: CHAIN_IDS[env].cross,
        };

        let estimate: GasEstimate | null = null;
        if (estimateGas) {
          try {
            estimate = await estimateGas({ ...tx, from: owner });
          } catch {
            estimate = null; // 추정 실패는 지갑 추정에 위임
          }
          if (flowIdRef.current !== flowId) return null; // 대기 중 닫힘/재오픈됨 — 새 플로우 상태를 건드리지 않는다.
        }

        setStatus("confirm");
        return {
          tx,
          estimate,
          expectedAmount0: simulated.amount0,
          expectedAmount1: simulated.amount1,
        };
      } catch (err) {
        if (flowIdRef.current !== flowId) return null; // 대기 중 닫힘/재오픈됨 — 새 플로우 상태를 건드리지 않는다.
        setStatus("error");
        setError(toDisplayWalletError(err, "Failed to prepare withdraw."));
        return null;
      }
    }, [env, estimateGas, target, walletAddress]);

  const confirmWithdraw = React.useCallback(
    async (prepared: CrossdWithdrawPrepared) => {
      if (!sendTransaction) {
        setStatus("error");
        setError("Withdraw is not available for this wallet.");
        return;
      }

      try {
        setStatus("submitting");
        setError("");
        trackFunnelOnce("submitted");

        const hash = await sendTransaction({
          ...prepared.tx,
          gas:
            prepared.estimate?.gasLimit && prepared.estimate.gasLimit > 0n
              ? prepared.estimate.gasLimit
              : undefined,
          maxFeePerGas:
            prepared.estimate?.maxFeePerGas &&
            prepared.estimate.maxFeePerGas > 0n
              ? prepared.estimate.maxFeePerGas
              : undefined,
          maxPriorityFeePerGas:
            prepared.estimate?.maxPriorityFeePerGas &&
            prepared.estimate.maxPriorityFeePerGas > 0n
              ? prepared.estimate.maxPriorityFeePerGas
              : undefined,
        });

        setTxHash(hash);
        setStatus("success");
        const withdrawnTokenId = target?.position.token_id;
        if (withdrawnTokenId) {
          setWithdrawnTokenIds((prev) => {
            const next = new Set(prev);
            next.add(withdrawnTokenId);
            return next;
          });
        }
        trackFunnelOnce("confirmed");
        invalidatePortfolioQueries();
      } catch (err) {
        setStatus("error");
        setError(toDisplayWalletError(err, "Failed to request withdraw."));
        trackFunnelOnce("confirmed", {
          status: "failure",
          reason: normalizeFailureReason(err),
        });
      }
    },
    [invalidatePortfolioQueries, sendTransaction, target, trackFunnelOnce],
  );

  const copyHash = React.useCallback(async () => {
    if (!txHash || typeof navigator === "undefined" || !navigator.clipboard) {
      return;
    }
    try {
      await navigator.clipboard.writeText(txHash);
      setCopied(true);
      clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopied(false), 1_400);
    } catch {
      setCopied(false);
    }
  }, [txHash]);

  return {
    canWithdrawInline,
    isOpen,
    target,
    withdrawnTokenIds,
    status,
    txHash,
    error,
    copied,
    hasEstimator: !!estimateGas,
    openWithdraw,
    closeWithdraw,
    prepareWithdraw,
    confirmWithdraw,
    copyHash,
    trackFunnelOnce,
  };
}
